// Social Queue API Service
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const socialQueueApi = {
  // Get social posts
  getPosts: async (userId, status = null, teamId = null) => {
    let url = `${BACKEND_URL}/api/social/posts?user_id=${userId}`;
    if (status) url += `&status=${status}`;
    if (teamId) url += `&team_id=${teamId}`;
    
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch posts');
    return response.json();
  },

  // Create draft posts from a text brief
  createDraft: async (userId, prompt, platforms, count = 1, styleOverride = null, profileContext = null) => {
    const response = await fetch(`${BACKEND_URL}/api/social/draft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        prompt,
        platforms,
        count,
        style_override: styleOverride,
        // Profile context for personalized generation
        profile_name: profileContext?.name || null,
        profile_type: profileContext?.type || 'business',
        profile_description: profileContext?.description || null,
        profile_website: profileContext?.website || null,
        custom_instructions: profileContext?.instructions || null
      })
    });
    if (!response.ok) throw new Error('Failed to create draft');
    return response.json();
  },

  // Create posts from uploaded images
  createFromImages: async (userId, files, platforms, mode = 'single', brief = '', autoFit = false) => {
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('platforms', platforms.join(','));
    formData.append('mode', mode);
    formData.append('brief', brief);
    formData.append('auto_fit_per_platform', autoFit ? '1' : '0');
    
    files.forEach(file => {
      formData.append('image_files', file);
    });
    
    const response = await fetch(`${BACKEND_URL}/api/social/from-image`, {
      method: 'POST',
      body: formData
    });
    if (!response.ok) throw new Error('Failed to create from images');
    return response.json();
  },

  // Update a post
  updatePost: async (postId, updates) => {
    const response = await fetch(`${BACKEND_URL}/api/social/posts/${postId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error('Failed to update post');
    return response.json();
  },

  // Generate image for a post
  generateImage: async (postId) => {
    const response = await fetch(`${BACKEND_URL}/api/social/posts/${postId}/generate-image`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to generate image');
    return response.json();
  },

  // Publish a post
  publishPost: async (postId, userId, scheduleFor = null) => {
    const response = await fetch(`${BACKEND_URL}/api/social/posts/${postId}/publish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        schedule_for: scheduleFor
      })
    });
    if (!response.ok) throw new Error('Failed to publish post');
    return response.json();
  },

  // Delete a post
  deletePost: async (postId) => {
    const response = await fetch(`${BACKEND_URL}/api/social/posts/${postId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete post');
    return response.json();
  },

  // Get assets
  getAssets: async (userId, category = null) => {
    let url = `${BACKEND_URL}/api/social/assets?user_id=${userId}`;
    if (category) url += `&category=${category}`;
    
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch assets');
    return response.json();
  },

  // Get connected accounts
  getConnectedAccounts: async (userId) => {
    const response = await fetch(`${BACKEND_URL}/api/social/connected-accounts?user_id=${userId}`);
    if (!response.ok) throw new Error('Failed to fetch connected accounts');
    return response.json();
  },

  // Get OAuth connect URL
  getConnectUrl: async (userId, platform) => {
    const response = await fetch(`${BACKEND_URL}/api/social/connect/${platform}?user_id=${userId}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.detail || 'Failed to get connect URL');
      throw error;
    }
    return response.json();
  },

  // Disconnect a social account
  disconnectAccount: async (connectionId, userId) => {
    const response = await fetch(`${BACKEND_URL}/api/social/disconnect/${connectionId}?user_id=${userId}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.detail || 'Failed to disconnect account');
      throw error;
    }
    return response.json();
  }
};

export default socialQueueApi;
